/** Precedence between the two transformer attribute overlays. Both draw the
 *  same triangle at the same size and position, so with both enabled one
 *  silently conceals the other. Resolved in data rather than by draw order:
 *  canvas order follows Leaflet stamp id, which changes when a toggled-off
 *  layer remounts, so ordering cannot carry a correctness rule.
 *
 *  Structural types (rather than the app's GeoJSON interfaces) keep this
 *  module dependency-free and unit-testable, matching oms/outageMapMarkers.ts. */

interface FeatureLike {
  properties?: Record<string, unknown> | null
}

/**
 * Drop `loading` features whose transformer already appears in `measured`.
 * Measured AMI data supersedes the flat model estimate; transformers with no
 * AMI keep their estimated dot because only `loading` has a feature for them.
 *
 * Both layers key on `properties.id` = transformer id.
 *
 * Generic over the whole collection type (rather than a structural type with
 * an index signature) so the app's GeoJSONFeatureCollection interface passes:
 * TS does not give interfaces an implicit index signature, so a parameter
 * typed `{ features: T[]; [key: string]: unknown }` would reject it.
 */
export function supersedeByMeasured<C extends { features: FeatureLike[] }>(
  loading: C | undefined,
  measured: { features: FeatureLike[] } | undefined,
  measuredEnabled: boolean,
): C | undefined {
  if (!loading) return loading
  if (!measuredEnabled || !measured) return loading

  const covered = new Set(measured.features.map(f => f.properties?.id))
  return {
    ...loading,
    features: loading.features.filter(f => !covered.has(f.properties?.id)),
  } as C
}
